import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';

export function Sheet({ title, subtitle, tone = 'sky', onClose, children, footer, testId }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  const head = tone === 'rose' ? 'bg-rose-600' : tone === 'amber' ? 'bg-amber-500' : 'bg-sky-600';
  return createPortal(
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center" data-testid={testId}>
      <div className="bg-white dark:bg-slate-900 w-full sm:max-w-md h-[100dvh] sm:h-auto sm:max-h-[92vh] sm:rounded-2xl flex flex-col overflow-hidden">
        <div className={`${head} text-white px-4 py-3 flex items-center justify-between`}>
          <div>
            <h2 className="text-lg font-black">{title}</h2>
            {subtitle && <p className="text-xs opacity-90">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="p-2 -mr-2" data-testid={`${testId}-close`}><X className="h-6 w-6" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">{children}</div>
        {footer && <div className="p-4 border-t border-slate-200 dark:border-slate-700 pb-[max(1rem,env(safe-area-inset-bottom))]">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function SubmitButton({ onClick, busy, disabled, tone = 'sky', children, testId }) {
  const c = tone === 'rose' ? 'bg-rose-600 hover:bg-rose-700' : tone === 'amber' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700';
  return (
    <button type="button" onClick={onClick} disabled={busy || disabled} data-testid={testId}
      className={`w-full h-14 rounded-xl text-white text-lg font-black flex items-center justify-center gap-2 disabled:opacity-50 ${c}`}>
      {busy && <Loader2 className="h-5 w-5 animate-spin" />}{children}
    </button>
  );
}

export function DoneScreen({ title, lines, onClose, testId }) {
  const [closing, setClosing] = useState(false);
  const { t } = useTranslation();
  return (
    <Sheet title={title} onClose={onClose} testId={testId}
      footer={<SubmitButton onClick={() => { setClosing(true); onClose(); }} busy={closing} testId={`${testId}-ok`}>{t('startStop.okClose')}</SubmitButton>}>
      <div className="flex flex-col items-center text-center py-6 gap-3">
        <div className="h-24 w-24 rounded-full bg-emerald-100 flex items-center justify-center animate-[pulse_1.2s_ease-in-out_2]">
          <CheckCircle2 className="h-14 w-14 text-emerald-600" />
        </div>
        {lines.map((l) => <p key={l} className="text-base text-slate-700 dark:text-slate-200">{l}</p>)}
      </div>
    </Sheet>
  );
}
